// Felder-Mapping einer Vertragsvorlage: welche Platzhalter zugeordnet werden
// und welche gespeicherten Zuordnungen inaktiv sind. Herausgelöst aus
// VertragsAutomationStudio.jsx (automatedMappingPlaceholders, staleFieldKeys;
// 23.09.2026, reines Verschieben) - die mobile Mapping-Seite zeigt dasselbe.

import { BUYER_FIELD_PLACEHOLDERS, LAGEPLAN_PLACEHOLDER, normalizePlaceholderName } from './vertragsStand';
import { istAufhebungsfeld } from './aufhebung';
import { istWerkvertragsfeld } from './werkvertrag';

const KAEUFER = new Set(BUYER_FIELD_PLACEHOLDERS);

function istKaeuferfeld(name) {
  return KAEUFER.has(normalizePlaceholderName(name));
}

/**
 * Die Platzhalter, die ins Mapping gehören: im Käufer-Ablauf ohne die
 * Käuferfelder (die füllt der Bearbeiter, nicht Bitrix). Ebenso nie die
 * Angaben zum aufzuhebenden Vertrag (Aufhebungsvereinbarung, src/lib/aufhebung.js)
 * und das Angebotsdatum des Werkvertrags (src/lib/werkvertrag.js).
 *
 * Mit `lageplanBild` steht {{Lageplan_Bild}} immer in der Liste, wie jedes
 * andere Feld (Wunsch des Nutzers, 05.10.2026) – auch wenn die gespeicherte
 * Vorlage den Platzhalter im Bild der Anlage 2 verloren hat. Das passiert,
 * wenn der Editor die Vorlage speichert, nachdem das Bild schon durch die
 * Ersatzgrafik getauscht war; dann fehlte die Zeile, und das Bild ließ sich
 * keinem Bitrix-Feld zuordnen. Das Bild holt beim Erzeugen die Absicherung in
 * api/_lib/contractLageplan.js aus dem zugeordneten Feld (auch wenn das <img>
 * ganz fehlt: fehlendesLageplanBild).
 */
export function zuzuordnendePlatzhalter(platzhalter, kaeuferFluss, { lageplanBild = false } = {}) {
  const liste = Array.isArray(platzhalter) ? platzhalter : [];
  const wegfallend = (name) => istAufhebungsfeld(name) || istWerkvertragsfeld(name) || (kaeuferFluss && istKaeuferfeld(name));
  const zuordnen = liste.some(wegfallend) ? liste.filter((name) => !wegfallend(name)) : liste;
  if (!lageplanBild || zuordnen.includes(LAGEPLAN_PLACEHOLDER)) return zuordnen;
  return [...zuordnen, LAGEPLAN_PLACEHOLDER].sort((a, b) => a.localeCompare(b, 'de'));
}

/**
 * Ob {{Lageplan_Bild}} ins Mapping gehört: immer, wenn die Vorlage einen
 * Lageplan-Bereich hat – „für jeden Vertrag, genauso wie die anderen
 * Mapping-Felder“ (Nutzer, 05.10.2026). Die Absicherung nach dem Erzeugen
 * holt das Bild aus diesem Feld, auch wenn im Bild ein anderer, ungefüllter
 * Platzhalter steht.
 * @param {{ available?: boolean }} lageplan aus getLageplanImageInfo
 */
export function lageplanBildZuordnen(lageplan) {
  return Boolean(lageplan && lageplan.available);
}

/**
 * Alle Zeilen des Felder-Mappings in einer Liste (seit 05.10.2026, Wunsch des
 * Nutzers: „Es muss festgeschrieben sein, außer ich drücke einen Knopf“).
 *
 * Vorher standen gespeicherte Zuordnungen, deren Platzhalter in der geladenen
 * Vorlage fehlt, abgesondert unter „Inaktive Felder“ – beim Großspeicher JZ
 * 13 von 28, weil die gespeicherte Vorlage noch die Platzhalter des
 * Kleinspeichers trägt. Das sah aus, als wären sie entfernt worden. Jetzt
 * bleibt jede Zeile an ihrem Platz (alphabetisch), bleibt bearbeitbar und
 * trägt nur einen Hinweis; weg kommt sie allein über den Knopf ✕.
 * Lageplan_Bild steht immer oben, damit es nicht zwischen den anderen untergeht.
 * @param {{ zuzuordnen: string[], felder: object, lageplanVerfuegbar: boolean }} angaben
 * @returns {Array<{ name: string, inVorlage: boolean }>}
 */
export function mappingZeilen({ zuzuordnen, felder, lageplanVerfuegbar }) {
  const ausVorlage = Array.isArray(zuzuordnen) ? zuzuordnen : [];
  const vorlage = new Set(ausVorlage);
  // lageplanVerfuegbar: false, damit ein gespeichertes Lageplan_Bild nie herausfällt.
  const gespeichert = inaktiveMappingFelder({ felder, zuzuordnen: ausVorlage, lageplanVerfuegbar: false });
  const namen = Array.from(new Set([...ausVorlage, ...gespeichert])).sort((a, b) => {
    if (a === LAGEPLAN_PLACEHOLDER) return -1;
    if (b === LAGEPLAN_PLACEHOLDER) return 1;
    return a.localeCompare(b, 'de');
  });
  return namen.map((name) => ({
    name,
    inVorlage: vorlage.has(name) || (name === LAGEPLAN_PLACEHOLDER && Boolean(lageplanVerfuegbar)),
  }));
}

/**
 * Inaktive Felder: gespeichert, aber nicht mehr in der Vorlage. Käuferfelder
 * zählen nie dazu, das Lageplan-Feld nicht, solange die Vorlage einen
 * Lageplan-Bereich hat.
 * @param {{ felder: object, zuzuordnen: string[], lageplanVerfuegbar: boolean }} angaben
 * @returns {string[]} sortiert
 */
export function inaktiveMappingFelder({ felder, zuzuordnen, lageplanVerfuegbar }) {
  if (!felder || typeof felder !== 'object') return [];
  const vorhanden = new Set(zuzuordnen || []);
  return Object.keys(felder)
    .filter((key) => !istKaeuferfeld(key))
    .filter((key) => !vorhanden.has(key) && !(key === LAGEPLAN_PLACEHOLDER && lageplanVerfuegbar))
    .sort((a, b) => a.localeCompare(b, 'de'));
}

function belegt(wert) {
  return typeof wert === 'string' && wert.trim() !== '';
}

/** Wie viele Zuordnungen tatsächlich etwas eintragen – leere zählen nicht. */
export function belegteFelder(felder) {
  if (!felder || typeof felder !== 'object') return 0;
  return Object.values(felder).filter(belegt).length;
}

/**
 * Gespeicherte Zuordnungen, die der Entwurf leeren würde (belegt → leer oder
 * weg), sortiert.
 */
export function geleerteZuordnungen(gespeichert, entwurf) {
  if (!gespeichert || typeof gespeichert !== 'object') return [];
  const neu = entwurf && typeof entwurf === 'object' ? entwurf : {};
  return Object.keys(gespeichert)
    .filter((key) => belegt(gespeichert[key]) && !belegt(neu[key]))
    .sort((a, b) => a.localeCompare(b, 'de'));
}

/**
 * Rückfrage vor dem Speichern, wenn dabei belegte Zuordnungen verloren gingen
 * – sonst null.
 *
 * Anlass 05.10.2026: Das Mapping des Großspeicher-Vertrags stand plötzlich
 * leer da. contracts-am ersetzt das Mapping beim Speichern vollständig; ein
 * leerer Entwurf überschreibt also alles. Einzelne Felder bewusst zu leeren
 * (oder inaktive zu entfernen) soll ohne Rückfrage gehen, deshalb erst ab drei
 * Feldern oder wenn danach gar nichts mehr belegt wäre.
 */
export function mappingSpeichernWarnung(gespeichert, entwurf) {
  const geleert = geleerteZuordnungen(gespeichert, entwurf);
  if (geleert.length === 0) return null;
  const bleibt = belegteFelder(entwurf);
  if (geleert.length < 3 && bleibt > 0) return null;
  const beispiele = geleert.slice(0, 8).map((name) => `{{${name}}}`).join(', ');
  const rest = geleert.length > 8 ? ` und ${geleert.length - 8} weitere` : '';
  return `Speichern leert ${geleert.length} gespeicherte Zuordnung${geleert.length === 1 ? '' : 'en'} `
    + `(${beispiele}${rest}); danach ${bleibt === 0 ? 'ist keine' : `sind nur noch ${bleibt}`} belegt. Trotzdem speichern?`;
}

/**
 * Vorschläge für das Felder-Mapping: Platzhalter → Bitrix-Feld des Deals.
 *
 * Anlass 05.10.2026: Das Mapping des Großspeicher-Vertrags war leer, eine
 * Sicherung gab es nicht. Abgeleitet aus den Feldern des Deals (Bitrix
 * crm.deal.fields) und geprüft an echten BESS-Deals:
 * - Einmalzahlung pro MW steht in „Pacht BESS u. KS EZ Gesamt pro MW“
 *   (19.000, 30.000 …), nicht in „Pacht BESS EZ (pro MW)“ – das steht fast
 *   überall auf dem Vorgabewert 1000. Deal 10406: 30000 ↔ „dreißigtausend“.
 * - Gemarkung, Flur, Flurstück aus den einzelnen Textfeldern des Vertrags
 *   (dieselben wie DEFAULT_EXPOSE_BITRIX_MAPPING), nicht aus den Listenfeldern.
 * - „Grundbuch von“ ist die Gemarkung – so steht es auch in § 1.2 der Vorlage.
 * - Eigentümer („ET“) ist der Vertragspartner: dieselbe Anschrift.
 * Eingesetzt wird nur in leere Felder; gespeichert erst, wenn der Bearbeiter
 * geprüft und „Speichern“ geklickt hat.
 */
export const MAPPING_VORSCHLAEGE = {
  Firma: 'UF_CRM_1752409111883',
  'Name Vertragspartner': 'UF_CRM_1752311736133',
  'Straße, Hausnummer': 'UF_CRM_1752311766494',
  'Postleitzahl, Ort': 'UF_CRM_1752311814010',
  'Straße, Hausnummer, ET': 'UF_CRM_1752311766494',
  'Postleitzahl, Ort, ET': 'UF_CRM_1752311814010',
  'Straße, Hausnummer - Fläche': 'UF_CRM_1752318833571',
  'Straße Hausnummer Fläche': 'UF_CRM_1752318833571',
  'Postleitzahl, Ort – Fläche': 'UF_CRM_1752313081525',
  'PLZ, Ort Fläche': 'UF_CRM_1752313081525',
  Amtsgericht: 'UF_CRM_1752319556769',
  'Grundbuch von': 'UF_CRM_1752323992',
  Gemarkung: 'UF_CRM_1752323992',
  Gemeinde: 'UF_CRM_1752311946586',
  Flur: 'UF_CRM_1752321437',
  Flurstück: 'UF_CRM_1752324013',
  'Größe Flurstück': 'UF_CRM_1752312683120',
  Bundesland: 'UF_CRM_1729875617742',
  Frist_Baubeginn: 'UF_CRM_1752312749081',
  Kontoinhaber: 'UF_CRM_1759669680494',
  'Name Bank': 'UF_CRM_1759669718975',
  IBAN: 'UF_CRM_1759669696329',
  BIC: 'UF_CRM_1759669706293',
  Eigentumsanteil: 'UF_CRM_1759669728699',
  // Großspeicher Einmalzahlung (EZ)
  Pacht_einmalig_pro_MW: 'UF_CRM_1754080144',
  Pacht_in_Worten_BESS: 'UF_CRM_1752320028463',
  'Nutzungsentgelt VL': 'UF_CRM_1752320167037',
  Pacht_in_Worten_BESS_VL: 'UF_CRM_1754078167469',
  // Großspeicher Jahreszahlung (JZ)
  'Pacht_jährlich_pro_MW': 'UF_CRM_1751026411',
  Pacht_in_Worten: 'UF_CRM_1752320028463',
  'Pacht_jährlich_pro_MW_VL': 'UF_CRM_1752320167037',
  'Pacht_in_Worten VL': 'UF_CRM_1754078167469',
};

/**
 * Vorschläge, die nur für einen Vertrag gelten. Das Lageplan-Bild liegt je
 * Produkt in einem eigenen Bitrix-Feld; für den Großspeicher ist es
 * „Lageplan Bild BESS“ (Nutzer, 05.10.2026). Die anderen Verträge bekommen
 * keinen Vorschlag – ohne Zuordnung bleibt es dort beim bisherigen Weg.
 */
const LAGEPLAN_BILD_BESS = 'UF_CRM_1755781964';
export const MAPPING_VORSCHLAEGE_JE_VERTRAG = {
  'pachtvertrag-grossspeicher-ez': { [LAGEPLAN_PLACEHOLDER]: LAGEPLAN_BILD_BESS },
  'pachtvertrag-grossspeicher-jz': { [LAGEPLAN_PLACEHOLDER]: LAGEPLAN_BILD_BESS },
  // Werkliefervertrag (30.10.2026): installierte Leistung = „Gesamt kWp“ aus
  // PV*SOL (pvsolExtractor.js, gesamt_kwp). Nur hier - der Projektrechtevertrag
  // nennt mit demselben Platzhalter eine unverbindliche Planungsangabe.
  'werkliefervertrag-pv': { 'Projektleistung kWp': 'UF_CRM_1750195757' },
};

/** Die Vorschläge für einen Vertrag: die allgemeinen plus die eigenen. */
export function mappingVorschlaegeFuer(vertragsSchluessel) {
  return {
    ...MAPPING_VORSCHLAEGE,
    ...(MAPPING_VORSCHLAEGE_JE_VERTRAG[String(vertragsSchluessel || '')] || {}),
  };
}

/**
 * Setzt Vorschläge in die leeren Felder des Entwurfs – nur für Platzhalter,
 * die die Vorlage hat. Belegte Felder bleiben, wie sie sind – außer sie tragen
 * genau den Vorschlag mit vorangestelltem „DEAL.“: So setzte die erste Fassung
 * (05.10.2026) die Werte ein, der Nutzer will nur die reine Feldkennung.
 * @returns {{ felder: object, eingesetzt: string[] }}
 */
export function mappingMitVorschlaegen(entwurf, platzhalter, vorschlaege = MAPPING_VORSCHLAEGE) {
  const felder = { ...(entwurf && typeof entwurf === 'object' ? entwurf : {}) };
  const eingesetzt = [];
  (Array.isArray(platzhalter) ? platzhalter : []).forEach((name) => {
    const vorschlag = vorschlaege[name];
    if (!vorschlag) return;
    const bisher = String(felder[name] || '').trim();
    if (belegt(bisher) && bisher !== `DEAL.${vorschlag}`) return;
    felder[name] = vorschlag;
    eingesetzt.push(name);
  });
  return { felder, eingesetzt };
}

/**
 * Frühere Stände aus contracts-am (op=snapshots/op=snapshot) für die Anzeige:
 * neueste zuerst. Ein Stand ist das Mapping, wie es bis zu seinem Zeitpunkt
 * galt – gesichert, bevor jemand darüber gespeichert hat.
 */
export function fruehereStaende(eintraege) {
  return (Array.isArray(eintraege) ? eintraege : [])
    .filter((eintrag) => eintrag && eintrag.id)
    .map((eintrag) => ({
      id: String(eintrag.id),
      zeit: eintrag.zeit || null,
      felder: eintrag.felder && typeof eintrag.felder === 'object' ? eintrag.felder : null,
      belegt: eintrag.felder && typeof eintrag.felder === 'object' ? belegteFelder(eintrag.felder) : null,
    }))
    .sort((a, b) => String(b.zeit || b.id).localeCompare(String(a.zeit || a.id)));
}
